import React from 'react';
import { Box, Card, CardHeader, CardContent, Typography, CircularProgress } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { translate as t } from 'hooks/useTranslations';
import { useRiskPoolOrgContext } from '../../RiskPoolOrgContext';
import { usePolicyContext } from '../PolicyContext';
import useLexipolUpdates from '../_data_sources/useLexipolUpdates';
import { CHART_COLORS } from '../_constants';
import { neutral } from '_lex/_foundations/phxColors';

interface LegendItemProps {
  color: string;
  label: string;
  value: string;
}

function LegendItem({ color, label, value }: LegendItemProps) {
  return (
    <Box
      sx={{
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
        gap: 2,
        display: 'inline-flex',
      }}
    >
      <Box
        sx={{
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: 1,
          display: 'inline-flex',
        }}
      >
        <Box
          sx={{
            width: 16,
            height: 16,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              left: 4,
              top: 4,
              position: 'absolute',
              background: color,
              borderRadius: '50%',
            }}
          />
        </Box>
        <Box
          sx={{
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            gap: 1,
            display: 'inline-flex',
          }}
        >
          <Typography
            sx={{
              color: neutral[500],
              fontSize: 12,
              fontFamily: 'Inter',
              fontWeight: 400,
              lineHeight: '16.8px',
            }}
          >
            {label}
          </Typography>
        </Box>
      </Box>
      <Typography
        sx={{
          alignSelf: 'stretch',
          textAlign: 'center',
          color: neutral[700],
          fontSize: 14,
          fontFamily: 'Inter',
          fontWeight: 500,
          lineHeight: '21px',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export function LexipolUpdatesChart() {
  const { orgId, queryParams } = useRiskPoolOrgContext();
  const { manualId } = usePolicyContext();
  const { data, isLoading } = useLexipolUpdates(orgId, {
    ...queryParams,
    manualId: manualId !== 'all' ? manualId : undefined,
  });

  if (isLoading) {
    return (
      <Card sx={{ borderRadius: 3, border: `1px solid ${neutral[300]}`, minWidth: 331 }}>
        <CardHeader title={t('dashboard.riskPool.riskPoolOrg.lexipolUpdates.title')} />
        <CardContent>
          <Box sx={{ width: '100%', height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return null;
  }

  const total = data.acceptedCount + data.modifiedCount + data.rejectedCount;

  const chartData: Array<{
    id: string;
    value: number;
    label: string;
  }> = [
    {
      id: 'accepted',
      value: data.acceptedCount,
      label: t('dashboard.riskPool.riskPoolOrg.lexipolUpdates.accepted'),
    },
    {
      id: 'modified',
      value: data.modifiedCount,
      label: t('dashboard.riskPool.riskPoolOrg.lexipolUpdates.modified'),
    },
    {
      id: 'rejected',
      value: data.rejectedCount,
      label: t('dashboard.riskPool.riskPoolOrg.lexipolUpdates.rejected'),
    },
  ];

  const chartColors = [CHART_COLORS.accepted, CHART_COLORS.modified, CHART_COLORS.rejected];

  const legendData = chartData.map((item, index) => ({
    label: item.label,
    value: item.value,
    percent: total > 0 ? Math.round((item.value / total) * 100) : 0,
    color: chartColors[index],
  }));

  return (
    <Card sx={{ borderRadius: 3, border: `1px solid ${neutral[300]}`, minWidth: 331 }}>
      <CardHeader title={t('dashboard.riskPool.riskPoolOrg.lexipolUpdates.title')} />
      <CardContent>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <PieChart
            colors={chartColors}
            series={[
              {
                data: chartData,
                innerRadius: 60,
                outerRadius: 100,
                paddingAngle: 0,
                cornerRadius: 0,
                startAngle: 0,
                endAngle: 360,
              },
            ]}
            margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
            height={200}
            slotProps={{
              legend: { hidden: true },
            }}
          />
          <Box
            sx={{
              alignSelf: 'stretch',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 8,
              display: 'inline-flex',
              pt: 10,
            }}
          >
            <Box
              sx={{
                flex: '1 1 0',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 5,
                display: 'flex',
              }}
            >
              {legendData.map((item) => (
                <LegendItem
                  key={item.label}
                  color={item.color}
                  label={item.label}
                  value={`${item.percent}%`}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default LexipolUpdatesChart;
